import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import { VideoPlayerLayout, VideoPlayerLayoutSkeleton } from "@/components/VideoPlayerLayout";
import { useSharedTutorialParam } from "@/hooks/useUrlParams";
import { useCourseItem } from "@/hooks/useCourses";
import { useVideos, useCourseVideos, useVideoIndex } from "@/hooks/useVideos";
import { useAsync } from "@/hooks/useAsync";
import { useAuth } from "@/lib/auth";
import * as api from "@/lib/api";

export function SharedTutorialPage() {
  const shareToken = useSharedTutorialParam();
  const { user, loading: authLoading } = useAuth();
  const trackedShareTokenRef = useRef<string | null>(null);

  // Resolve share token -> course_id directly via a single-row query
  const { data: shareData, loading: shareLoading } = useAsync(
    () => api.resolveCourseShareToken(shareToken!),
    shareToken ? [shareToken] : null,
  );

  const courseId = shareData?.course_id ?? null;

  const { data: course, loading: courseLoading } = useCourseItem(courseId);
  const { data: allVideos, loading: videosLoading } = useVideos();
  const { data: videoIndex } = useVideoIndex();
  const courseVideos = useCourseVideos(allVideos, courseId);

  const loading = shareLoading || courseLoading || videosLoading;
  const courseName = course?.name ?? "Shared Tutorial";
  const instructorName = (course as any)?.Instructor?.name ?? "Khusbu Gemmati";

  useEffect(() => {
    if (
      !shareToken ||
      trackedShareTokenRef.current === shareToken ||
      authLoading ||
      !!user ||
      loading ||
      !course
    ) {
      return;
    }

    trackedShareTokenRef.current = shareToken;
    void api.trackShareOpen("course", shareToken);
  }, [authLoading, user, shareToken, loading, course]);

  if (!shareToken) {
    return (
      <div className="flex min-h-screen flex-col bg-[#f6f5f6] text-[#1c171b]">
        <SharedTopbar title="Invalid Share Link" />
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="text-center">
            <p className="text-[0.96rem] text-[#70696f]">No share token was provided.</p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-[0.9rem] bg-[#762a5c] px-6 py-3 text-[0.93rem] font-semibold text-white"
            >
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-[#f6f5f6] text-[#1c171b]">
        <SharedTopbar title="Loading..." />
        <main className="flex flex-1 flex-col">
          <VideoPlayerLayoutSkeleton />
        </main>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex min-h-screen flex-col bg-[#f6f5f6] text-[#1c171b]">
        <SharedTopbar title="Tutorial Not Found" />
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="text-center">
            <p className="text-[0.96rem] text-[#70696f]">
              This shared tutorial could not be found or the link has expired.
            </p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-[0.9rem] bg-[#762a5c] px-6 py-3 text-[0.93rem] font-semibold text-white"
            >
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#f6f5f6] text-[#1c171b]">
      <SharedTopbar title={courseName} />
      <main className="flex flex-1 flex-col">
        <VideoPlayerLayout
          courseVideos={courseVideos}
          videoIndex={videoIndex}
          instructorName={instructorName}
        />
      </main>
    </div>
  );
}

function SharedTopbar({ title }: { title: string }) {
  return (
    <header className="flex min-h-[4.5rem] items-center gap-5 border-b border-[#e2dce0] bg-white px-[clamp(1rem,3vw,3rem)] max-[767px]:min-h-[4rem]">
      <a
        href="https://bollyrhythm.com"
        className="flex shrink-0 items-center"
      >
        <img
          src="/logo-dark.png"
          alt="BollyRhythm"
          className="h-7 w-auto"
        />
      </a>
      <div className="h-5 w-px bg-[#e2dce0]" />
      <h1 className="m-0 truncate font-[family-name:var(--font-display)] text-[1.15rem] font-medium leading-[1.2] text-[#321b29]">
        {title}
      </h1>
    </header>
  );
}
